import { apiClient } from "@/api/axiosClient.ts";
import { handleApiError } from "./common.ts";
import type { PaginatedResponse, PaginationParams } from "./common.ts";

// Reuse visit status values for ORL domain
export type OrlVisitStatus =
  | "scheduled"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "no_show";

export interface OrlVisitListItem {
  id: string; // UUID
  patient: string; // patient UUID
  patient_name: string;
  doctor: number;
  doctor_name: string;
  visit_date: string; // YYYY-MM-DD
  visit_type?: string;
  status?: OrlVisitStatus;
  chief_complaint?: string;
  primary_diagnosis?: string;
  follow_up_date?: string; // YYYY-MM-DD
  created_at?: string;
  updated_at?: string;
}

export interface OrlVisitDetail extends OrlVisitListItem {
  symptom_duration?: string;
  symptoms?: string;
  physical_exam_notes?: string;
  primary_diagnosis?: string;
  diagnosis_notes?: string;
  treatment_plan?: string;
  medications?: string;
  follow_up_notes?: string;
  attachments?: string;
  examinations_enabled?: boolean;
  examinations_notes?: string;
  department?: "orl" | "ophthalmology";
  intervention_diagnosis?: string;
  intervention_type?: string;
  intervention_notes?: string;
  nasal_presentation?: string[];
  nasal_presentation_other?: string;
  orl_examinations?: string[];
  canalicular_probe?: string;
  canalicular_probe_findings?: string;
}

export interface OrlVisitPayload {
  patient: string; // UUID
  doctor: number;
  visit_date: string; // YYYY-MM-DD
  visit_type?: string;
  status?: OrlVisitStatus;
  department?: "orl" | "ophthalmology";
  chief_complaint?: string;
  symptoms?: string;
  symptom_duration?: string;
  physical_exam_notes?: string;
  primary_diagnosis?: string;
  diagnosis_notes?: string;
  treatment_plan: string;
  medications?: string;
  follow_up_date?: string; // YYYY-MM-DD
  follow_up_notes?: string;
  attachments?: string;
  examinations_enabled?: boolean;
  examinations_notes?: string;
  intervention_diagnosis?: string;
  intervention_type?: string;
  intervention_notes?: string;
  nasal_presentation?: string[];
  nasal_presentation_other?: string;
  orl_examinations?: string[];
  canalicular_probe?: string;
  canalicular_probe_findings?: string;
}

export interface CreateOrlVisitRequest extends OrlVisitPayload {}
export interface UpdateOrlVisitRequest extends OrlVisitPayload {}

export interface OrlVisitsQueryParams extends PaginationParams {
  status?: OrlVisitStatus;
  search?: string;
  diagnosis?: string;
}

export type OrlVisitsListResponse = PaginatedResponse<OrlVisitListItem>;

const basePath = (patientId: string) =>
  `/clinic/patients/${patientId}/orl-visits/`;

export const orlVisitsApi = {
  /**
   * Get paginated list of ORL visits (patient_id is required)
   */
  async getVisits(
    patientId: string,
    params: OrlVisitsQueryParams,
  ): Promise<OrlVisitsListResponse> {
    if (!patientId || patientId.trim() === "") {
      throw new Error("patientId is required to list ORL visits");
    }
    try {
      const response = await apiClient.get<OrlVisitsListResponse>(
        basePath(patientId),
        { params },
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch ORL visits");
    }
  },

  /**
   * Get a single ORL visit by ID
   */
  async getVisit(patientId: string, id: string): Promise<OrlVisitDetail> {
    try {
      const response = await apiClient.get<OrlVisitDetail>(
        `${basePath(patientId)}${id}/`,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch ORL visit");
    }
  },

  /**
   * Create a new ORL visit
   */
  async createVisit(
    patientId: string,
    data: CreateOrlVisitRequest,
  ): Promise<OrlVisitDetail> {
    try {
      const response = await apiClient.post<OrlVisitDetail>(
        basePath(patientId),
        data,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to create ORL visit");
    }
  },

  /**
   * Update an ORL visit (PUT)
   */
  async updateVisit(
    patientId: string,
    id: string,
    data: UpdateOrlVisitRequest,
  ): Promise<OrlVisitDetail> {
    try {
      const response = await apiClient.put<OrlVisitDetail>(
        `${basePath(patientId)}${id}/`,
        data,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to update ORL visit");
    }
  },

  /**
   * Partially update an ORL visit (PATCH)
   */
  async partialUpdateVisit(
    patientId: string,
    id: string,
    data: Partial<UpdateOrlVisitRequest>,
  ): Promise<OrlVisitDetail> {
    try {
      const response = await apiClient.patch<OrlVisitDetail>(
        `${basePath(patientId)}${id}/`,
        data,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to update ORL visit");
    }
  },

  /**
   * Delete an ORL visit
   */
  async deleteVisit(patientId: string, id: string): Promise<void> {
    try {
      await apiClient.delete(`${basePath(patientId)}${id}/`);
    } catch (error) {
      handleApiError(error, "Failed to delete ORL visit");
    }
  },
};
